export const heroChips = [
  '6 complete manuals',
  '5 strategic bonuses',
  '180+ ready-to-run drills',
  '52-week periodization matrix',
  'U12 through U19',
]

export const heroStats = [
  { value: '1,200+', label: 'Academy coaches across Europe, USA & LATAM' },
  { value: '6 + 5', label: 'Manuals and bonuses included' },
  { value: '4.9/5', label: 'Director rating' },
  { value: '52 weeks', label: 'Planned, phase by phase' },
]

export const shortQuotes = [
  {
    quote:
      'I planned our entire season in one evening. Every session now has a clear objective and my staff finally coach the same way.',
    name: 'Marco D.',
    role: 'Academy Director · Porto',
  },
  {
    quote:
      'I stopped inventing sessions on the drive to training. I open the manual and everything is there: organized, structured, professional.',
    name: 'Callum B.',
    role: 'Head of Coaching · Manchester',
  },
  {
    quote:
      'Our U15s progressed more in one block than in the previous season. Parents can see the path now.',
    name: 'Andrés V.',
    role: 'Club Owner · Bogotá',
  },
]

export const problems = [
  'Coach A teaches one way, Coach B teaches another — there is no club identity',
  'Your players have talent, but they stagnate tactically because sessions are random',
  'You spend hours every week building session plans from scratch',
  'Parents don\u2019t see a structured path to improvement, so they take their business elsewhere',
  'You feel you have to be on every pitch to guarantee quality control',
  'You are trading your coaching energy for administrative burnout',
]

export const solutionMetrics = [
  { label: 'Practical pages', value: '240+', unit: 'Practical pages' },
  { label: 'Ready-to-run drills', value: '180+', unit: 'Ready-to-run drills' },
  { label: 'Session templates', value: '40+', unit: 'Session templates' },
  { label: 'Complete manuals', value: '6', unit: 'Complete manuals' },
]

export const coverage = [
  'Annual Periodization',
  'Pressing & Defensive Block',
  'Build-Up & Possession',
  'Finishing & Striker Play',
  'Midfield Box-to-Box',
  'Assessment & Progress Tracking',
]

export type Volume = {
  number: string
  title: string
  body: string
  topics: string[]
}

export const volumes: Volume[] = [
  {
    number: 'VOL. 01',
    title: 'The Annual Periodization Matrix',
    body: 'A 52-week roadmap divided into four phases — Build, Develop, Peak, Recover. Never wonder what to train again.',
    topics: ['Build', 'Develop', 'Peak', 'Recover'],
  },
  {
    number: 'VOL. 02',
    title: '40+ High-Performance 11v11 Drills',
    body: 'Designed specifically for full-pitch scenarios, from tactical rondos to coordinated pressing traps.',
    topics: [
      'Tactical Rondos',
      'Pressing Traps',
      'Transitions',
      'Full-Pitch Games',
    ],
  },
  {
    number: 'VOL. 03',
    title: 'Professional Session Templates',
    body: 'Ready-to-print plans your coaches can take straight to the pitch — warm-up, main block, competitive finish.',
    topics: ['Warm-Up', 'Main Block', 'Competitive Finish', 'Coaching Points'],
  },
  {
    number: 'VOL. 04',
    title: 'The "Cognitive Football" Framework',
    body: 'Drills that train the brain, not just the feet. Scanning, decision-making and spatial awareness under pressure.',
    topics: [
      'Scanning',
      'Decision-Making',
      'Spatial Awareness',
      'Game Pressure',
    ],
  },
  {
    number: 'VOL. 05',
    title: 'Performance Tracking Suite',
    body: 'Professional-grade templates to measure player growth and justify premium tuition fees.',
    topics: [
      'Player Profiles',
      'Progress Reports',
      'Level Criteria',
      'Parent Reviews',
    ],
  },
  {
    number: 'VOL. 06',
    title: 'The Club Identity Playbook',
    body: 'How to install the system across your staff so every coach on every pitch delivers the same standard.',
    topics: [
      'Staff Onboarding',
      'Quality Control',
      'Session Audits',
      'Coach Development',
    ],
  },
]

export type Bonus = {
  number: string
  title: string
  body: string
  value: string
  cover: string
}

export const bonuses: Bonus[] = [
  {
    number: 'Bonus 01',
    title: 'The Pressing Masterclass',
    body: '20 drills that build a coordinated press: triggers, cover shadows, and collective recovery from the front line back.',
    value: '$29',
    cover:
      'https://cdn.magicpatterns.com/uploads/j8CEaqWr9mZziMrP7ZGr8T/futbo_1.webp',
  },
  {
    number: 'Bonus 02',
    title: 'The Striker\u2019s Intelligence System',
    body: '32 drills on movement in the box, timing of runs, and finishing under realistic full-pitch pressure.',
    value: '$39',
    cover:
      'https://cdn.magicpatterns.com/uploads/kh8Y7aPHnwqTKXFVVybTuW/futbo_2.webp',
  },
  {
    number: 'Bonus 03',
    title: 'The Build-Up Blueprint',
    body: '25 drills for playing out from the back with structure — third-man combinations, angles, and breaking the first line.',
    value: '$29',
    cover:
      'https://cdn.magicpatterns.com/uploads/n24qWC1tqHmaNiLTEsorLa/futbo_3.webp',
  },
  {
    number: 'Bonus 04',
    title: 'The Midfielder\u2019s Box-to-Box System',
    body: '30 drills covering scanning, receiving on the half-turn, and controlling tempo across both boxes.',
    value: '$39',
    cover:
      'https://cdn.magicpatterns.com/uploads/2g1NA1cJRwVEBMEKLmWmEL/futbo_4.webp',
  },
  {
    number: 'Bonus 05',
    title: 'The Defensive Resilience Masterclass',
    body: '35 drills to build a compact, aggressive defensive block that holds shape for 90 minutes.',
    value: '$39',
    cover:
      'https://cdn.magicpatterns.com/uploads/3ozdvpdXz2qDQgN7rviUKR/futbo_5.webp',
  },
]

export const packageIncludes = [
  'The Total Academy System main manual',
  '5 strategic bonus manuals',
  '180+ ready-to-run 11v11 drills',
  '52-week annual periodization matrix',
  'Print-ready session templates',
  'Lifetime access to every file',
]

export const testimonials = [
  {
    quote:
      'I planned our entire season in one evening. Every session has a clear objective and my staff finally coach the same way. This changed how we run the club.',
    name: 'Marco D.',
    initials: 'MD',
    role: 'Academy Director · Porto',
    featured: true,
  },
  {
    quote:
      'Two blocks in and our U15s have made more visible progress than in the whole previous season. The progression genuinely works.',
    name: 'Callum B.',
    initials: 'CB',
    role: 'Head of Coaching · Manchester',
    featured: false,
  },
  {
    quote:
      'Before, I was hunting for drills online every night. Now I open the manual and everything is there — organized, ready, professional.',
    name: 'Andrés V.',
    initials: 'AV',
    role: 'Club Owner · Bogotá',
    featured: false,
  },
  {
    quote:
      'The tracking templates changed how we talk to parents. We show concrete, measurable progress instead of opinions.',
    name: 'Lena K.',
    initials: 'LK',
    role: 'Program Coordinator · Hamburg',
    featured: false,
  },
  {
    quote:
      'As a young director I was overwhelmed. This system gave our academy professional credibility from week one.',
    name: 'Tomás R.',
    initials: 'TR',
    role: 'Academy Director · Buenos Aires',
    featured: false,
  },
]

export const guaranteeBadges = [
  'Simple refund',
  'No forms to fill',
  'Keep all bonuses',
]

export const formatPoints = [
  'Every manual and bonus is a high-resolution PDF',
  'No video, no audio, no physical book to wait for',
  'Print any page or hand it out digitally to your staff',
  'Download instantly after checkout',
]

export const faqs = [
  {
    q: 'Is this digital or physical?',
    a: 'Completely digital. Every volume and bonus is a high-resolution PDF — no video, no audio, no physical book. You get instant access and can print as many copies as your coaching staff needs.',
  },
  {
    q: 'Is this suitable for amateur clubs?',
    a: 'Yes. While the drills are pro-level, the instructions are clear enough for any coach to implement on a full-size pitch.',
  },
  {
    q: 'Does it work for all age groups?',
    a: 'The curriculum is optimized for competitive youth from U12 to U19 playing 11v11 football.',
  },
  {
    q: 'Can I adapt the sessions to my squad?',
    a: 'Every session template is built to be adjusted — change the numbers, pitch size, or constraints while keeping the objective and the phase of the periodization intact.',
  },
  {
    q: 'What if I\u2019m not completely satisfied?',
    a: 'Email us within 7 days and we refund every penny, no questions asked. You keep the bonuses.',
  },
]

export const comparison = [
  {
    without: '100+ hours a year spent planning sessions',
    with: 'Minutes per week · everything already prepared',
  },
  {
    without: 'Improvised drills with no progression',
    with: '180+ structured drills across a 52-week plan',
  },
  {
    without: 'Every coach teaching a different way',
    with: 'One curriculum · staff follow the manual, you stop micro-managing',
  },
  {
    without: 'Parents asking where the results are',
    with: 'Tracking templates and progress reports ready to send',
  },
  {
    without: 'Priced like a recreational club',
    with: 'A professional curriculum that justifies premium tuition',
  },
]

export const images = {
  bundle:
    'https://cdn.magicpatterns.com/uploads/8CTHAEEad82pzDQ11pC3qn/ChatGPT_Image_2_sept_2026,_04_44_45_p.m..png',
  manual:
    'https://cdn.magicpatterns.com/uploads/cmoEKb27dKoDbUVwFkgs4h/futbo_aura.webp',
  pitch:
    'https://cdn.magicpatterns.com/patterns/generated-images/e99eccf1-621a-4b6f-a46e-faa8253d049a.jpg',
  sessionPlans:
    'https://cdn.magicpatterns.com/patterns/generated-images/12999a1e-0ee7-4d24-9353-70826ef57ff4.jpg',
}

export const pricing = {
  price: '$27',
  compare: '$372',
  savings: '$345',
  discount: '93%',
}

export const checkoutUrl =
  'https://the-method-of-fotball.impultienda.ar/checkout'

export const contactEmail = 'support@totalacademysystems.com'
